import React, { useEffect, useRef, useState } from "react";
import { sosContent } from "../content";

// Peças pequenas e compartilhadas da LP (no projeto de LPs ficavam em
// _lib/picture, _components/{selos,rec,avatar} e components/shared/*).

export const IMG = "/images/sos-aprova";

/** <picture> com AVIF e fallback WebP. `src` sem extensão. */
export function Pic({ src, alt, width, height, className, priority = false }) {
  return (
    <picture>
      <source type="image/avif" srcSet={`${src}.avif`} />
      <source type="image/webp" srcSet={`${src}.webp`} />
      <img
        src={`${src}.webp`}
        alt={alt}
        width={width}
        height={height}
        decoding="async"
        loading={priority ? "eager" : "lazy"}
        {...(priority ? { fetchpriority: "high" } : {})}
        className={className}
      />
    </picture>
  );
}

/** Os três selos (data, ao vivo, gratuito), no hero e no card lateral. */
export function Selos({ pequeno = false }) {
  return (
    <div className={`sos-selos${pequeno ? " sos-selos--peq" : ""}`}>
      {sosContent.selos.map((s) => (
        <span key={s.label} className={`sos-selo sos-selo--${s.tone}`}>
          {s.blink && <i className="sos-blink" aria-hidden="true" />}
          {s.label}
        </span>
      ))}
    </div>
  );
}

const pad = (n) => String(n).padStart(2, "0");

/** Selo "REC 00:02:45": conta desde a abertura da página. */
export function Rec({ className = "" }) {
  const [s, setS] = useState(165);
  useEffect(() => {
    const id = window.setInterval(() => setS((v) => v + 1), 1000);
    return () => window.clearInterval(id);
  }, []);
  return (
    <span className={`sos-rec ${className}`} aria-hidden="true">
      <i className="sos-blink" />
      REC {pad(Math.floor(s / 3600))}:{pad(Math.floor((s % 3600) / 60))}:{pad(s % 60)}
    </span>
  );
}

/** Chave do professor a partir do nome: minúsculo, sem acento, hífen no lugar de espaço. */
export function chaveProf(nome) {
  return nome
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-");
}

export function iniciais(nome) {
  return nome
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/** Avatar redondo do professor; sem foto, mostra as iniciais. Convidado ganha anel amarelo. */
export function Avatar({ nome, convidado = false, tamanho = 32, className }) {
  const foto = sosContent.programacao.avatares[chaveProf(nome)];
  return (
    <span className={`sos-av${convidado ? " sos-av--convidado" : ""}${className ? ` ${className}` : ""}`} style={{ width: tamanho, height: tamanho }}>
      {foto ? <Pic src={`${IMG}/profs/${foto}`} alt="" width={96} height={96} /> : <span aria-hidden="true">{iniciais(nome)}</span>}
    </span>
  );
}

/** Contagem regressiva: null antes de montar e depois de zerar. */
export function useCountdown(target) {
  const [parts, setParts] = useState(null);
  useEffect(() => {
    const targetMs = new Date(target).getTime();
    const diff = () => {
      const delta = targetMs - Date.now();
      if (delta <= 0) return null;
      const t = Math.floor(delta / 1000);
      return { days: Math.floor(t / 86400), hours: Math.floor((t % 86400) / 3600), minutes: Math.floor((t % 3600) / 60), seconds: t % 60 };
    };
    setParts(diff());
    const id = window.setInterval(() => {
      const next = diff();
      setParts(next);
      if (!next) window.clearInterval(id);
    }, 1000);
    return () => window.clearInterval(id);
  }, [target]);
  return parts;
}

/** Onda de frequência no "O" do logo, redesenhada a cada quadro. */
export function Onda() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const t0 = performance.now();
    let raf = 0;
    const draw = () => {
      const t = (performance.now() - t0) / 1000;
      let d = "";
      const g = 0.55 + 0.45 * Math.sin(t * 1.7) * Math.sin(t * 0.9 + 1);
      for (let x = 722; x <= 963; x += 3) {
        const u = (x - 722) / 241;
        const env = Math.sin(Math.PI * u);
        const v = Math.sin(x * 0.21 + t * 9) * 0.55 + Math.sin(x * 0.083 - t * 5.3) * 0.3 + Math.sin(x * 0.47 + t * 14) * 0.18;
        d += (d ? " L" : "M") + x + " " + (363 + 58 * env * g * v).toFixed(1);
      }
      el.setAttribute("d", d);
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, []);
  return (
    <path ref={ref} d="M720 363 L965 363" fill="none" stroke="#fff" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" style={{ filter: "drop-shadow(0 0 6px rgba(108,203,239,0.9))" }} />
  );
}

/**
 * `generate_lead` no dataLayer, o mesmo evento das LPs. Com `aoConcluir`, espera
 * o GTM confirmar (ou 800 ms) antes de seguir, para a navegação não perder o evento.
 */
export function dispararGenerateLead(formulario, extra, aoConcluir) {
  let pendente = aoConcluir;
  const seguir = () => {
    if (!pendente) return;
    const f = pendente;
    pendente = undefined;
    f();
  };
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "generate_lead",
    form_name: formulario,
    ...extra,
    ...(aoConcluir ? { eventCallback: seguir, eventTimeout: 800 } : {}),
  });
  if (aoConcluir) window.setTimeout(seguir, 800);
}

/**
 * Reveal ao rolar: blocos .sos-reveal fora da tela entram com fade + 18px,
 * 60 ms de atraso entre os que aparecem juntos.
 */
export function Reveal() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || !Element.prototype.animate) return;
    const pendentes = new Map();
    document.querySelectorAll(".sos-reveal").forEach((el) => {
      if (el.getBoundingClientRect().top < window.innerHeight * 0.92) return;
      const a = el.animate([{ opacity: 0, translate: "0 18px" }, { opacity: 1, translate: "0 0" }], {
        duration: 560,
        easing: "cubic-bezier(0.2,0.7,0.2,1)",
        fill: "backwards",
      });
      a.pause();
      pendentes.set(el, a);
    });
    if (!pendentes.size) return;
    const io = new IntersectionObserver(
      (entries) => {
        let k = 0;
        entries.forEach((e) => {
          const a = pendentes.get(e.target);
          if (!e.isIntersecting || !a) return;
          if (a.effect) a.effect.updateTiming({ delay: k++ * 60 });
          a.play();
          pendentes.delete(e.target);
          io.unobserve(e.target);
        });
      },
      { threshold: 0.12 }
    );
    pendentes.forEach((_, el) => io.observe(el));
    return () => {
      io.disconnect();
      pendentes.forEach((a) => a.cancel());
    };
  }, []);
  return null;
}

const NUMEROS_OFICIAIS_VENDAS = [
  "(48) 98855-4675",
  "(48) 98815-6770",
  "(48) 98824-5734",
  "(48) 96136-0617",
  "(48) 96136-5821",
  "(48) 98616-5821",
  "(48) 96136-0591",
  "(48) 98867-0666",
  "(48) 98819-6562",
  "(48) 98875-1248",
];

const telHref = (n) => `tel:+55${n.replace(/\D/g, "")}`;

/** Bloco "Números oficiais de vendas" do rodapé (estilos em sos-base.css). */
export function NumerosOficiais({ margemTopo }) {
  return (
    <div role="group" aria-label="Números oficiais de vendas" className="at-num at-num--escuro at-num--divisor" style={{ marginTop: margemTopo }}>
      <p className="at-num-titulo">
        <svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.3-.4.7-1.4.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.9 11.9 0 0 0 4.6 4c1.7.7 2.3.8 3.2.7.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.1-1.2l-.4-.3Z" />
        </svg>
        Números oficiais de vendas
      </p>
      <ul className="at-num-lista">
        {NUMEROS_OFICIAIS_VENDAS.map((n) => (
          <li key={n}>
            <a href={telHref(n)}>{n}</a>
          </li>
        ))}
      </ul>
    </div>
  );
}
